import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import { getHelpCardTitle } from 'common/helpCardHelpers';

import { formatPhoneNumber } from 'common/formatters';
import { SUNROOOM_CUSTOMER_PHONE_NUMBER } from 'common/sunroomConstants';
import { PARTNER_TYPE_AGENT } from 'common/partnerConstants';

import { MEDIA_QUERY_LARGE_SCREEN } from 'client/constants/mediaQueries';

import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrIcon from 'client/components/SrIcon';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrLink from 'client/components/SrLink/SrLink';
import SrExternalLink from 'client/components/SrExternalLink';
import SrPartnerPageV2, {
  SR_PARTNER_PAGE_THEME_BLANK_STATE,
} from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrContactCard from 'client/components/SrContactCard/SrContactCard';
import SrHelpCardSection from 'client/components/SrHelpCardSection/SrHelpCardSection';
import {
  HowToTalkToClientsAboutSunroom,
  PartnerAgentOwnerMarketingPageChecklist,
} from 'client/components/PartnerAgentOwnerMarketingPage';

const SrPartnerDashboardBlankState = ({
  navbarIconUrl,
  to,
  salesRep,
  currentPartner,
  currentUser,
  helpCards,
  appUrl,
}) => {
  useEffect(() => {
    global.scrollTo(0, 0);
  }, []);

  const isLargeScreen = useMediaQuery({
    query: MEDIA_QUERY_LARGE_SCREEN,
  });

  return (
    <SrPartnerPageV2
      pageHeader="Leasing Portal"
      navbarImage={navbarIconUrl}
      theme={SR_PARTNER_PAGE_THEME_BLANK_STATE}
      paddingBottom="50px"
      currentPartner={currentPartner}
      currentUser={currentUser}
      navbarMaxWidth="100%"
      hasWatermark
    >
      <SrFlex wrap="wrap" justify="center" marginTop="36px">
        <SrBox width="375px" margin={isLargeScreen ? '0 0 65px 0' : '35px 0 0 0'}>
          <SrBox height="auto" width="180px" margin="0 auto">
            <img
              width="100%"
              height="100%"
              alt="property photos"
              src="/images/leasing-portal-blank-state.png"
            />
          </SrBox>
          <SrBox margin="12px auto 32px auto" width="100%" maxWidth="300px">
            <SrText
              align="center"
              display="block"
              fontSize="30px"
              lineHeight="36px"
              font="greycliff"
              spacing="0"
              weight="600"
            >
              Lease your client's property stress-free.
            </SrText>
          </SrBox>
          <SrBox margin="0 auto" width="302px">
            <SrLink to={to}>
              <SrButton
                type="button"
                width="263px"
                height="60px"
                theme={SR_BUTTON_THEME_ADOBE_GREEN}
                hasBoxShadow
              >
                Market Property Now
              </SrButton>
            </SrLink>
          </SrBox>
          <SrBox margin="25px 0 0 86px">
            <SrExternalLink link="https://sunroomleasing.com/pages/how-it-works-partner-portal/">
              <SrFlex align="center">
                <SrText font="greycliff" color="blue300" weight="600">
                  See How Sunroom Works
                </SrText>
                <SrBox margin="4px 0 0 7px">
                  <SrIcon className="arrow-right-blue" />
                </SrBox>
              </SrFlex>
            </SrExternalLink>
          </SrBox>
        </SrBox>
        <SrBox margin={isLargeScreen ? '0 10px' : '24px 0 0 125px'} />
        {salesRep && salesRep.name && (salesRep.phone || salesRep.email) ? (
          <SrContactCard
            name={salesRep.name}
            profiles={[
              {
                name: salesRep.name,
                imageUrl: salesRep.profileImage || '/images/nativebanner-app-icon.jpg',
                title: 'Sunroom Sales Executive',
              },
            ]}
            imageSubtitles={[formatPhoneNumber(salesRep.phone), salesRep.email]}
            headline="Contact your dedicated Sales Executive"
            buttons={[
              salesRep.email && {
                label: 'Email',
                iconUrl: '/images/mail-icon-blue.svg',
                link: `mailto:${salesRep.email}`,
              },
              salesRep.phone && {
                label: 'Call',
                iconUrl: '/images/phone-icon-blue.svg',
                link: `tel:${salesRep.phone}`,
              },
              salesRep.calendlyLink && {
                label: 'Schedule Call',
                iconUrl: '/images/calendar-icon-blue.svg',
                link: salesRep.calendlyLink,
              },
            ]}
          />
        ) : (
          <SrContactCard
            profiles={[
              {
                name: 'Support Team',
                imageUrl: '/images/nativebanner-app-icon.jpg',
              },
            ]}
            imageSubtitles={[
              formatPhoneNumber(SUNROOOM_CUSTOMER_PHONE_NUMBER),
              'hello@sunroomrentals.com',
            ]}
            buttons={[
              {
                label: 'Email',
                iconUrl: '/images/mail-icon-blue.svg',
                link: 'mailto:hello@sunroomrentals.com',
              },
              {
                label: 'Call',
                iconUrl: '/images/phone-icon-blue.svg',
                link: `tel:${SUNROOOM_CUSTOMER_PHONE_NUMBER}`,
              },
            ]}
          />
        )}
      </SrFlex>
      {currentPartner.type === PARTNER_TYPE_AGENT && (
        <SrFlex
          wrap="wrap"
          justify={isLargeScreen ? 'between' : 'center'}
          margin={isLargeScreen ? '90px 0 130px' : '150px 0 130px'}
          padding="10px"
        >
          <SrBox padding={isLargeScreen ? '0 10px' : '0'} maxWidth="100%" boxSizing="border-box">
            <HowToTalkToClientsAboutSunroom partner={currentPartner} appUrl={appUrl} />
          </SrBox>
          <SrBox
            order={isLargeScreen ? -1 : null}
            margin={isLargeScreen ? '31px 0 49px 0' : '31px 0 24px 125px'}
            width={isLargeScreen ? '400px' : null}
          >
            <PartnerAgentOwnerMarketingPageChecklist partner={currentPartner} />
          </SrBox>
        </SrFlex>
      )}
      {helpCards && helpCards.length > 0 && (
        <SrBox marginTop="80px" padding="0 8px">
          <SrHelpCardSection
            helpCards={helpCards}
            title={getHelpCardTitle({ partner: currentPartner })}
          />
        </SrBox>
      )}
    </SrPartnerPageV2>
  );
};

SrPartnerDashboardBlankState.propTypes = {
  navbarIconUrl: PropTypes.string,
  to: PropTypes.string.isRequired,
  currentPartner: PropTypes.object,
  currentUser: PropTypes.object,
  helpCards: PropTypes.array,
  appUrl: PropTypes.string.isRequired,
};

SrPartnerDashboardBlankState.displayName = 'SrPartnerDashboardBlankState';

export default SrPartnerDashboardBlankState;
